/**
 * TILE-037: Global Indexing Engine (Phase 2: Ritual-Integrated Unified Search Index)
 * 
 * Unified, precomputed search index that powers:
 * - Predictive search (TILE-036)
 * - Future AI search tiles (TILE-052+)
 * - Quiz → PDP → Blog → Ingredients → Ritual pages
 * - Smart ranking for rituals / pains / symptoms
 * - Ingredient → benefit mapping
 * - Semantic scoring without external APIs
 * 
 * Loads after LCP via deferUntilAfterLCP() to never block performance.
 */

(function() {
  'use strict';

  // Normalize text for matching (lowercase, trim, remove punctuation)
  function normalizeText(text) {
    if (!text) return '';
    return String(text)
      .toLowerCase()
      .trim()
      .replace(/[^\w\s]/g, ' ')
      .replace(/\s+/g, ' ');
  }

  // Calculate keyword match score (0-1)
  function calculateKeywordScore(query, keywords) {
    if (!query || !keywords || keywords.length === 0) return 0;
    
    const normalizedQuery = normalizeText(query);
    const queryWords = normalizedQuery.split(/\s+/).filter(function(w) { return w.length > 0; });
    
    if (queryWords.length === 0) return 0;
    
    let matchCount = 0;
    let totalScore = 0;
    
    keywords.forEach(function(keyword) {
      const normalizedKeyword = normalizeText(keyword);
      const keywordWords = normalizedKeyword.split(/\s+/).filter(function(w) { return w.length > 0; });
      
      // Exact phrase match (highest score)
      if (normalizedQuery.includes(normalizedKeyword)) {
        totalScore += 1.0;
        matchCount++;
        return;
      }
      
      // Word-by-word match
      let wordMatches = 0;
      keywordWords.forEach(function(kw) {
        if (kw.length < 2) return;
        queryWords.forEach(function(qw) {
          if (qw.includes(kw) || kw.includes(qw)) {
            wordMatches++;
          }
        });
      });
      
      if (wordMatches > 0) {
        totalScore += wordMatches / keywordWords.length;
        matchCount++;
      }
    });
    
    if (matchCount === 0) return 0;
    
    // Normalize to 0-1 range
    return Math.min(1.0, totalScore / keywords.length);
  }

  // Build unified index
  const VELUNA_INDEX = {
    rituals: [
      {
        key: 'mind-calm',
        label: 'Mind Calm Ritual',
        icon: 'moon',
        keywords: ['mind calm', 'mind-calm', 'mc', 'anxiety', 'racing thoughts', 'racing mind', 'stress', 'calm', 'worry', 'overthinking', 'mental chatter'],
        benefit: 'Quiet your racing mind and fall asleep naturally',
        pains: ['racing thoughts', 'sleep onset anxiety', 'mental chatter', 'worry', 'overthinking', 'stress', 'cortisol spikes', 'nighttime anxiety'],
        benefits: ['deep calm', 'quiet mind', 'peaceful sleep', 'nervous system downregulation', 'mental clarity', 'stress relief'],
        ingredients: ['ashwagandha', 'l-theanine', 'magnesium glycinate', 'chamomile', 'passionflower', 'lemon balm'],
        archetype: 'The Overthinker - Someone whose mind races at bedtime, struggling with anxiety, worry, or mental chatter that prevents sleep onset.',
        emotionalPattern: 'Anxious, overwhelmed, mentally exhausted, seeking mental quiet and peace'
      },
      {
        key: 'deep-restore',
        label: 'Deep Restore Ritual',
        icon: 'sparkle',
        keywords: ['deep restore', 'deep-restore', 'dr', 'recovery', 'athlete', 'muscle', 'restore', 'physical recovery', 'body fatigue', 'post-workout'],
        benefit: 'Support muscle recovery and deeper sleep',
        pains: ['muscle tension', 'body fatigue', 'post-workout alertness', 'physical exhaustion', 'sore muscles', 'recovery', 'athletic performance'],
        benefits: ['muscle recovery', 'restorative sleep', 'physical restoration', 'deeper sleep', 'body repair', 'athletic recovery'],
        ingredients: ['magnesium glycinate', 'tart cherry', 'glycine', 'valerian root', 'hops'],
        archetype: 'The Athlete - Someone who needs physical recovery, deals with muscle tension, or struggles with post-workout alertness.',
        emotionalPattern: 'Physically exhausted, performance-focused, seeking restoration and recovery'
      },
      {
        key: 'sacred-soothe',
        label: 'Sacred Soothe Ritual',
        icon: 'star',
        keywords: ['sacred soothe', 'sacred-soothe', 'ss', 'beauty', 'skin', 'soothe', 'calm', 'radiant', 'evening ritual', 'self-care'],
        benefit: 'Calm evenings and radiant mornings',
        pains: ['evening restlessness', 'skin concerns', 'beauty sleep', 'evening anxiety', 'self-care needs'],
        benefits: ['radiant mornings', 'calm evenings', 'beauty sleep', 'skin health', 'evening tranquility', 'self-care ritual'],
        ingredients: ['chamomile', 'lavender', 'magnesium', 'collagen peptides', 'hyaluronic acid'],
        archetype: 'The Self-Care Seeker - Someone who values evening rituals, beauty sleep, and calm transitions into rest.',
        emotionalPattern: 'Self-care focused, ritual-oriented, seeking tranquility and radiance'
      },
      {
        key: 'circadian-reset',
        label: 'Circadian Reset Ritual',
        icon: 'arrow-down',
        keywords: ['circadian reset', 'circadian-reset', 'cr', 'jet lag', 'travel', 'shift', 'rhythm', 'sleep schedule', 'time zone', 'shift work'],
        benefit: 'Reset your sleep-wake cycle naturally',
        pains: ['circadian disruption', 'jet lag', 'shift work', 'irregular sleep schedule', 'time zone changes', 'sleep-wake cycle issues'],
        benefits: ['circadian alignment', 'sleep-wake cycle reset', 'natural rhythm', 'travel recovery', 'schedule adjustment'],
        ingredients: ['melatonin', 'magnesium', 'l-theanine', 'valerian root'],
        archetype: 'The Traveler/Shift Worker - Someone dealing with jet lag, shift work, or irregular sleep schedules that disrupt natural rhythms.',
        emotionalPattern: 'Disrupted, out of sync, seeking rhythm and alignment'
      }
    ],

    ingredients: [
      {
        name: 'Ashwagandha',
        keywords: ['ashwagandha', 'withania', 'adaptogen', 'stress', 'anxiety', 'cortisol'],
        effects: ['stress reduction', 'cortisol management', 'anxiety relief', 'adaptogenic support'],
        rituals: ['mind-calm'],
        benefits: ['deep calm', 'stress relief', 'nervous system support'],
        safetyNotes: 'Generally safe, may interact with thyroid medications'
      },
      {
        name: 'L-Theanine',
        keywords: ['l-theanine', 'theanine', 'green tea', 'calm', 'focus', 'relaxation'],
        effects: ['promotes relaxation', 'reduces anxiety', 'improves focus', 'calm alertness'],
        rituals: ['mind-calm', 'circadian-reset'],
        benefits: ['mental calm', 'anxiety reduction', 'focused relaxation'],
        safetyNotes: 'Very safe, no known interactions'
      },
      {
        name: 'Magnesium Glycinate',
        keywords: ['magnesium', 'magnesium glycinate', 'glycinate', 'muscle', 'recovery', 'relaxation'],
        effects: ['muscle relaxation', 'nervous system support', 'recovery', 'sleep quality'],
        rituals: ['mind-calm', 'deep-restore', 'sacred-soothe', 'circadian-reset'],
        benefits: ['muscle recovery', 'nervous system downregulation', 'deeper sleep'],
        safetyNotes: 'Safe at recommended doses, may cause digestive issues at high doses'
      },
      {
        name: 'Melatonin',
        keywords: ['melatonin', 'sleep hormone', 'circadian', 'jet lag', 'sleep onset'],
        effects: ['sleep onset support', 'circadian rhythm regulation', 'jet lag relief'],
        rituals: ['circadian-reset'],
        benefits: ['faster sleep onset', 'circadian alignment', 'travel support'],
        safetyNotes: 'Safe for short-term use, may cause drowsiness next day if overused'
      },
      {
        name: 'Chamomile',
        keywords: ['chamomile', 'calm', 'soothing', 'herbal', 'relaxation'],
        effects: ['calming', 'soothing', 'relaxation', 'digestive support'],
        rituals: ['mind-calm', 'sacred-soothe'],
        benefits: ['evening calm', 'relaxation', 'gentle sleep support'],
        safetyNotes: 'Very safe, may interact with blood thinners'
      },
      {
        name: 'Passionflower',
        keywords: ['passionflower', 'passiflora', 'anxiety', 'calm', 'herbal'],
        effects: ['anxiety reduction', 'calming', 'sleep support'],
        rituals: ['mind-calm'],
        benefits: ['anxiety relief', 'calm', 'sleep onset'],
        safetyNotes: 'Generally safe, may cause drowsiness'
      },
      {
        name: 'Valerian Root',
        keywords: ['valerian', 'valerian root', 'sleep', 'calm', 'herbal'],
        effects: ['sleep support', 'calming', 'anxiety reduction'],
        rituals: ['deep-restore', 'circadian-reset'],
        benefits: ['deeper sleep', 'calm', 'sleep quality'],
        safetyNotes: 'Safe for short-term use, may cause drowsiness'
      },
      {
        name: 'Tart Cherry',
        keywords: ['tart cherry', 'cherry', 'recovery', 'athlete', 'muscle'],
        effects: ['muscle recovery', 'anti-inflammatory', 'sleep quality'],
        rituals: ['deep-restore'],
        benefits: ['athletic recovery', 'muscle repair', 'deeper sleep'],
        safetyNotes: 'Very safe, natural source'
      },
      {
        name: 'Glycine',
        keywords: ['glycine', 'amino acid', 'recovery', 'sleep quality'],
        effects: ['sleep quality improvement', 'muscle recovery', 'cognitive support'],
        rituals: ['deep-restore'],
        benefits: ['deeper sleep', 'recovery', 'cognitive function'],
        safetyNotes: 'Very safe, naturally occurring amino acid'
      },
      {
        name: 'Lavender',
        keywords: ['lavender', 'aromatic', 'calm', 'soothing', 'beauty'],
        effects: ['calming', 'aromatic relaxation', 'soothing'],
        rituals: ['sacred-soothe'],
        benefits: ['evening tranquility', 'calm', 'beauty sleep'],
        safetyNotes: 'Very safe, primarily aromatic'
      }
    ],

    pains: [
      {
        name: 'Racing Thoughts',
        keywords: ['racing thoughts', 'racing mind', 'mental chatter', 'overthinking', 'mind won\'t stop'],
        rituals: ['mind-calm'],
        ingredients: ['ashwagandha', 'l-theanine', 'chamomile', 'passionflower'],
        severity: 'high'
      },
      {
        name: 'Sleep Onset Anxiety',
        keywords: ['anxiety', 'worry', 'stress', 'can\'t fall asleep', 'sleep anxiety'],
        rituals: ['mind-calm'],
        ingredients: ['ashwagandha', 'l-theanine', 'passionflower', 'chamomile'],
        severity: 'high'
      },
      {
        name: 'Nighttime Wakeups',
        keywords: ['wake up', 'wakeups', 'can\'t stay asleep', 'fragmented sleep', 'interrupted sleep'],
        rituals: ['deep-restore', 'mind-calm'],
        ingredients: ['magnesium glycinate', 'valerian root', 'glycine'],
        severity: 'high'
      },
      {
        name: 'Cortisol Spikes',
        keywords: ['cortisol', 'stress hormone', 'adrenaline', 'wired', 'alert at night'],
        rituals: ['mind-calm'],
        ingredients: ['ashwagandha', 'l-theanine', 'magnesium'],
        severity: 'high'
      },
      {
        name: 'Circadian Disruption',
        keywords: ['circadian', 'sleep schedule', 'rhythm', 'off schedule', 'irregular sleep'],
        rituals: ['circadian-reset'],
        ingredients: ['melatonin', 'magnesium', 'l-theanine'],
        severity: 'high'
      },
      {
        name: 'Muscle Tension',
        keywords: ['muscle tension', 'sore muscles', 'body tension', 'physical tension'],
        rituals: ['deep-restore'],
        ingredients: ['magnesium glycinate', 'tart cherry', 'glycine'],
        severity: 'medium'
      },
      {
        name: 'Post-Workout Alertness',
        keywords: ['post-workout', 'after exercise', 'can\'t sleep after workout', 'athlete sleep'],
        rituals: ['deep-restore'],
        ingredients: ['magnesium glycinate', 'tart cherry', 'glycine', 'valerian root'],
        severity: 'medium'
      },
      {
        name: 'Jet Lag',
        keywords: ['jet lag', 'travel', 'time zone', 'travel sleep', 'time change'],
        rituals: ['circadian-reset'],
        ingredients: ['melatonin', 'magnesium', 'l-theanine'],
        severity: 'high'
      },
      {
        name: 'Shift Work Disruption',
        keywords: ['shift work', 'night shift', 'irregular hours', 'work schedule'],
        rituals: ['circadian-reset'],
        ingredients: ['melatonin', 'magnesium'],
        severity: 'high'
      }
    ],

    benefits: [
      {
        name: 'Deep Calm',
        keywords: ['calm', 'deep calm', 'peaceful', 'tranquil', 'serene'],
        rituals: ['mind-calm', 'sacred-soothe'],
        ingredients: ['ashwagandha', 'l-theanine', 'chamomile', 'passionflower'],
        pains: ['racing thoughts', 'sleep onset anxiety', 'cortisol spikes']
      },
      {
        name: 'Restorative Sleep',
        keywords: ['restorative', 'restorative sleep', 'deep sleep', 'quality sleep', 'healing sleep'],
        rituals: ['deep-restore'],
        ingredients: ['magnesium glycinate', 'tart cherry', 'glycine', 'valerian root'],
        pains: ['nighttime wakeups', 'muscle tension', 'post-workout alertness']
      },
      {
        name: 'Nervous System Downregulation',
        keywords: ['nervous system', 'downregulation', 'parasympathetic', 'relaxation response', 'calm nervous system'],
        rituals: ['mind-calm'],
        ingredients: ['ashwagandha', 'l-theanine', 'magnesium glycinate'],
        pains: ['cortisol spikes', 'racing thoughts', 'sleep onset anxiety']
      },
      {
        name: 'Muscle Recovery',
        keywords: ['muscle recovery', 'recovery', 'athletic recovery', 'muscle repair', 'physical recovery'],
        rituals: ['deep-restore'],
        ingredients: ['magnesium glycinate', 'tart cherry', 'glycine'],
        pains: ['muscle tension', 'post-workout alertness', 'body fatigue']
      },
      {
        name: 'Radiant Mornings',
        keywords: ['radiant', 'radiant mornings', 'beauty sleep', 'glowing', 'vibrant'],
        rituals: ['sacred-soothe'],
        ingredients: ['chamomile', 'lavender', 'magnesium'],
        pains: ['evening restlessness', 'beauty sleep']
      },
      {
        name: 'Circadian Alignment',
        keywords: ['circadian', 'circadian alignment', 'sleep-wake cycle', 'natural rhythm', 'body clock'],
        rituals: ['circadian-reset'],
        ingredients: ['melatonin', 'magnesium', 'l-theanine'],
        pains: ['circadian disruption', 'jet lag', 'shift work disruption']
      }
    ],

    utilities: {
      // Score ritual match (0-1)
      scoreRitualMatch: function(term) {
        if (!term) return 0;
        const normalizedTerm = normalizeText(term);
        let maxScore = 0;
        
        VELUNA_INDEX.rituals.forEach(function(ritual) {
          const keywordScore = calculateKeywordScore(term, ritual.keywords);
          const painScore = calculateKeywordScore(term, ritual.pains);
          const benefitScore = calculateKeywordScore(term, ritual.benefits);
          const archetypeScore = calculateKeywordScore(term, [ritual.archetype, ritual.emotionalPattern]);
          
          const ritualScore = Math.max(
            keywordScore * 1.0,
            painScore * 0.8,
            benefitScore * 0.7,
            archetypeScore * 0.6
          );
          
          maxScore = Math.max(maxScore, ritualScore);
        });
        
        return maxScore;
      },

      // Score ingredient match (0-1)
      scoreIngredientMatch: function(term) {
        if (!term) return 0;
        let maxScore = 0;
        
        VELUNA_INDEX.ingredients.forEach(function(ingredient) {
          const keywordScore = calculateKeywordScore(term, ingredient.keywords);
          const effectScore = calculateKeywordScore(term, ingredient.effects);
          const benefitScore = calculateKeywordScore(term, ingredient.benefits);
          
          const ingredientScore = Math.max(
            keywordScore * 1.0,
            effectScore * 0.8,
            benefitScore * 0.7
          );
          
          maxScore = Math.max(maxScore, ingredientScore);
        });
        
        return maxScore;
      },

      // Score pain match (0-1)
      scorePainMatch: function(term) {
        if (!term) return 0;
        let maxScore = 0;
        
        VELUNA_INDEX.pains.forEach(function(pain) {
          const keywordScore = calculateKeywordScore(term, pain.keywords);
          const severityMultiplier = pain.severity === 'high' ? 1.2 : 1.0;
          
          const painScore = keywordScore * severityMultiplier;
          maxScore = Math.max(maxScore, Math.min(1.0, painScore));
        });
        
        return maxScore;
      },

      // Score benefit match (0-1)
      scoreBenefitMatch: function(term) {
        if (!term) return 0;
        let maxScore = 0;
        
        VELUNA_INDEX.benefits.forEach(function(benefit) {
          const keywordScore = calculateKeywordScore(term, benefit.keywords);
          maxScore = Math.max(maxScore, keywordScore);
        });
        
        return maxScore;
      },

      // Score keyword match (generic, 0-1)
      scoreKeyword: function(term, keywords) {
        if (!term || !keywords) return 0;
        return calculateKeywordScore(term, keywords);
      },

      // Semantic score - aggregates all scores into single ranking value
      semanticScore: function(term) {
        if (!term) return 0;
        
        const ritualScore = VELUNA_INDEX.utilities.scoreRitualMatch(term);
        const ingredientScore = VELUNA_INDEX.utilities.scoreIngredientMatch(term);
        const painScore = VELUNA_INDEX.utilities.scorePainMatch(term);
        const benefitScore = VELUNA_INDEX.utilities.scoreBenefitMatch(term);
        
        // Weighted combination (rituals weighted highest)
        const finalScore = 
          ritualScore * 3.0 +
          ingredientScore * 2.0 +
          painScore * 2.0 +
          benefitScore * 1.0;
        
        // Normalize to 0-1 range (max possible is 8.0)
        return Math.min(1.0, finalScore / 8.0);
      }
    }
  };

  // Expose globally
  window.__VELUNA_INDEX__ = VELUNA_INDEX;

  // Log initialization (only in dev)
  if (window.location.hostname === 'localhost' || window.location.hostname.includes('127.0.0.1')) {
    console.log('✅ Veluna Global Index loaded:', {
      rituals: VELUNA_INDEX.rituals.length,
      ingredients: VELUNA_INDEX.ingredients.length,
      pains: VELUNA_INDEX.pains.length,
      benefits: VELUNA_INDEX.benefits.length
    });
  }
})();
